import { useContext } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography, Box, visuallyHidden } from 'bonfire-kindling';
import { Page } from 'components';
import { ToastContext } from 'contexts';
import { locationsToRegionData } from 'helpers/locations';
import { useVendor, useMutateVendor } from 'helpers/vendor';
import { useFeatureFlags } from 'helpers/hooks';
import SettingsContainer from './SettingsContainer/SettingsContainer';
import AccountDetails from './AccountDetails';
import BusinessInfo from './BusinessInfo';
import LocationSettings from './LocationSettings';
import SubscriptionInfo from './SubscriptionInfo';
import TaxonomyCodes from './TaxonomyCodes';
import KeywordSettings from './KeywordSettings';
import TimezoneSettings from './TimezoneSettings';
import SavedSearchSettings from './SavedSearchSettings';

export default props => {
  const { t } = useTranslation();
  const { triggerToast } = useContext(ToastContext);
  const { isFlagEnabled } = useFeatureFlags();
  const { isLoading } = useVendor();
  const { mutateAsync, isLoading: isMutating } = useMutateVendor();

  const saveVendorData = (payload, resetRecommendedOpportunities = false) =>
    mutateAsync(
      { payload, resetRecommendedOpportunities },
      {
        onError: () => {
          triggerToast({ message: t('generic_error') });
        },
        onSuccess: () => {
          triggerToast({
            message: t('settings.save_success'),
            variant: 'success',
          });
        },
      }
    );

  const handleSubmitBusinessInfo = values => saveVendorData(values);
  const handleSubmitKeywordsData = keywords => saveVendorData({ keywords }, true);
  const handleSubmitLocationData = selectedLocations =>
    saveVendorData({ regionData: locationsToRegionData(selectedLocations) }, true);

  if (props.match.isExact) {
    return <Redirect to="/settings/account" />;
  }

  return (
    <Page>
      <Box display="flex" minHeight="100%">
        <Typography sx={visuallyHidden} component="h1">
          {t('settings.account_settings')}
        </Typography>
        <Box flexGrow={1} aria-live="polite">
          <SettingsContainer isLoading={isLoading}>
            <Switch>
              <Route path="/settings/account">
                <AccountDetails />
              </Route>
              <Route path="/settings/subscription">
                <SubscriptionInfo />
              </Route>
              <Route path="/settings/business">
                <BusinessInfo onSubmit={handleSubmitBusinessInfo} />
              </Route>
              <Route path="/settings/commodity-codes">
                <TaxonomyCodes />
              </Route>
              <Route path="/settings/keywords">
                <KeywordSettings onSubmit={handleSubmitKeywordsData} isMutating={isMutating} />
              </Route>
              <Route path="/settings/location">
                <LocationSettings onSubmit={handleSubmitLocationData} />
              </Route>
              {isFlagEnabled('FF_20_RELEASE_TOGGLE_PV_TIMEZONE_SETTINGS_PAGE') && (
                <Route path="/settings/timezone">
                  <TimezoneSettings />
                </Route>
              )}
              {isFlagEnabled('FF_25_RELEASE_TOGGLE_PV_SAVED_SEARCH') && (
                <Route path="/settings/saved">
                  <SavedSearchSettings />
                </Route>
              )}
              <Redirect to="/settings/account" />
            </Switch>
          </SettingsContainer>
        </Box>
      </Box>
    </Page>
  );
};
